import { useCallback, useEffect, useRef } from 'react';

import { buildContentPath, generateSlug } from '@confluence/route-manager-utils/entry-points/url';
import {
	VIEW_BLOG,
	EDIT_PAGE_V2,
	EDIT_BLOG_V2,
	COMPANY_HUB,
} from '@confluence/named-routes/entry-points/routes';
import { expVal } from '@confluence/feature-experiments';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';

type UseHeadingLinksPathBuilderProps = {
	contentId: string;
	spaceKey: string;
	contentType: string;
	title: string | null;
	createdDate?: string | null;
	isUnpublishedDraft?: boolean;
};
export const useHeadingLinksPathBuilder = ({
	contentId,
	spaceKey,
	contentType,
	title,
	createdDate,
	isUnpublishedDraft,
}: UseHeadingLinksPathBuilderProps) => {
	const titleRef = useRef(title);
	const createdDateRef = useRef(createdDate);

	useEffect(() => {
		titleRef.current = title;
	}, [title]);

	useEffect(() => {
		createdDateRef.current = createdDate;
	}, [createdDate]);

	const getHeadingLinkPath = useCallback((): string | null => {
		const isUnpublishedPage = contentType === 'page' && isUnpublishedDraft;
		const isUnpublishedBlog = contentType === 'blogpost' && isUnpublishedDraft;
		const isCompanyHub = isCompanyHubSpaceKey(spaceKey);

		if (isUnpublishedPage) {
			return EDIT_PAGE_V2.toUrl({
				contentId,
				spaceKey,
				contentType: 'pages',
			});
		}

		if (isUnpublishedBlog) {
			return EDIT_BLOG_V2.toUrl({
				contentId,
				spaceKey,
				contentType: 'blog',
			});
		}

		if (contentType === 'blogpost' && !createdDateRef.current) {
			return VIEW_BLOG.toUrl({
				contentId,
				spaceKey,
				slug: generateSlug(titleRef.current),
			});
		}

		if (isCompanyHub && expVal('company-hub-edit-heading-anchor-link-create', 'isEnabled', false)) {
			return COMPANY_HUB.toUrl();
		}

		return buildContentPath(
			spaceKey,
			contentId,
			contentType,
			createdDateRef.current ?? '',
			generateSlug(titleRef.current),
		);
	}, [contentId, spaceKey, contentType, isUnpublishedDraft]);

	return { getHeadingLinkPath };
};
